import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { dashboardStatusHexColors, getTaskFilterOptions } from "../../../../constants/integrationTypes";
import { HiChevronDown } from 'react-icons/hi';

interface StatusFilterWithColorsProps {
  statusFilters: string[];
  setStatusFilters: (filters: string[]) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  statusCounts?: Record<string, number>;
}

const StatusFilterWithColors: React.FC<StatusFilterWithColorsProps> = ({ statusFilters, setStatusFilters, isOpen, setIsOpen, statusCounts }) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const statusOptions = useMemo(() => getTaskFilterOptions(), []);

  const statusOptionsWithCounts = useMemo(() => {
    if (!statusCounts) return statusOptions;
    return statusOptions.map((opt: { value: string; label: string }) => {
      const countSuffix = opt.value !== '' ? ` (${statusCounts[opt.value] || 0})` : '';
      return {
        ...opt,
        label: opt.label + countSuffix
      };
    });
  }, [statusOptions, statusCounts]);

  // Calculate dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 200)
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    function handleClick(event: MouseEvent) {
      const target = event.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, setIsOpen]);

  const toggleStatus = (status: string) => {
    setStatusFilters(
      statusFilters.includes(status)
        ? statusFilters.filter(s => s !== status)
        : [...statusFilters, status]
    );
  };

  const getButtonLabel = () => {
    if (statusFilters.length === 0) return 'Status: All';
    if (statusFilters.length === 1) {
      const opt = statusOptions.find((o: { value: string; label: string }) => o.value === statusFilters[0]);
      return `Status: ${opt?.label || 'All'}`;
    }
    return `Status: ${statusFilters.length} selected`;
  };

  const dropdownContent = isOpen && (
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        minWidth: dropdownPosition.width,
        zIndex: 99999
      }}
      className="bg-white dark:bg-secondary-dark border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl overflow-hidden"
    >
      <div className="max-h-64 overflow-y-auto">
        {statusOptionsWithCounts.slice(1).map((opt: { value: string; label: string }) => {
          const statusColor = dashboardStatusHexColors[opt.value] || '#9ca3af';
          const isSelected = statusFilters.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggleStatus(opt.value)}
              className={`w-full px-3 py-2 text-sm cursor-pointer transition-colors duration-200
                         text-left text-gray-700 dark:text-gray-300 flex items-center gap-3
                         ${isSelected ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => { }}
                className="w-4 h-4 text-primary-light bg-gray-100 border-gray-300 rounded focus:ring-primary-light focus:ring-2 dark:bg-gray-600 dark:border-gray-500 cursor-pointer"
              />
              <span
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: statusColor }}
              />
              <span className="flex-1 truncate">{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={`w-[180px] px-3 py-2 text-left bg-white dark:bg-secondary-dark
                   border border-gray-300 dark:border-gray-600 rounded-lg text-sm
                   hover:border-gray-400 dark:hover:border-gray-500
                   focus:outline-none focus:ring-1 focus:ring-gray-300/30 dark:focus:ring-gray-400/30
                   text-gray-900 dark:text-white transition-colors duration-200 cursor-pointer h-[38px]`}
      >
        <div className="flex items-center justify-between h-full">
          <span className="text-gray-900 dark:text-white truncate">
            {getButtonLabel()}
          </span>
          <HiChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>
      {typeof document !== 'undefined' && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default StatusFilterWithColors;